import { PHONE_PLACEHOLDER } from "@sagot/constants";
import { Field, Input, Select, Textarea } from "@sagot/ui";
import { useId } from "react";
import type { UseFormRegister } from "react-hook-form";
import { TEXTAREA_ROWS } from "./constants";
import type { ActionField } from "./contract";
import { errorMessage, isMobileField, maxCharsOf } from "./form-schema";
import type { ActionLabels } from "./labels";

const AUTOCOMPLETE_BY_TYPE: Partial<Record<ActionField["type"], string>> = { email: "email", tel: "tel" };
const CONTROL_HEIGHT = "h-11";

export interface FieldControlProps {
  field: ActionField;
  register: UseFormRegister<Record<string, unknown>>;
  error: unknown;
  labels: ActionLabels;
}

function Control({ field, register, labels }: Omit<FieldControlProps, "error">) {
  const mobile = isMobileField(field);
  const required = field.required && !mobile ? true : undefined;
  if (field.type === "select") {
    return (
      <Select className={CONTROL_HEIGHT} aria-required={required} {...register(field.name)}>
        <option value="">{labels.choose}</option>
        {(field.options ?? []).map((option) => (
          <option key={option} value={option}>
            {field.option_labels?.[option] ?? option}
          </option>
        ))}
      </Select>
    );
  }
  if (field.type === "textarea") {
    return <Textarea rows={TEXTAREA_ROWS} maxLength={maxCharsOf(field)} aria-required={required} autoComplete={field.autocomplete} {...register(field.name)} />;
  }
  return (
    <Input
      className={CONTROL_HEIGHT}
      type={mobile ? "tel" : field.type === "email" ? "email" : "text"}
      inputMode={mobile ? "tel" : undefined}
      autoComplete={field.autocomplete ?? AUTOCOMPLETE_BY_TYPE[field.type]}
      placeholder={mobile ? PHONE_PLACEHOLDER : undefined}
      maxLength={maxCharsOf(field)}
      aria-required={required}
      {...register(field.name)}
    />
  );
}

/** Pick-any choices: a fieldset whose legend is the label, one ≥ 44px row per option. */
function ChoicesControl({ field, register, error }: Omit<FieldControlProps, "labels">) {
  const id = useId();
  const message = errorMessage(error);
  return (
    <fieldset className="min-w-0 border-0 p-0" aria-describedby={message ? `${id}-error` : undefined} aria-invalid={message ? true : undefined}>
      <legend className="label">
        {field.label}
        {field.required ? (
          <span aria-hidden="true" className="text-danger">
            {" "}
            *
          </span>
        ) : null}
      </legend>
      <div className="grid gap-1">
        {(field.options ?? []).map((option) => (
          <label key={option} className="flex min-h-11 items-center gap-2 text-sm">
            <input type="checkbox" className="checkbox size-5 shrink-0" value={option} {...register(field.name)} />
            <span>{field.option_labels?.[option] ?? option}</span>
          </label>
        ))}
      </div>
      {message ? (
        <p id={`${id}-error`} role="alert" className="mt-1 text-xs font-semibold text-danger">
          {message}
        </p>
      ) : null}
    </fieldset>
  );
}

/** One server-described field: label (required marker), control, hint and error wired for assistive tech. */
export function FieldControl({ field, register, error, labels }: FieldControlProps) {
  if (field.type === "checkboxes") return <ChoicesControl field={field} register={register} error={error} />;
  const mobile = isMobileField(field);
  const label = (
    <>
      {field.label}
      {field.required && !mobile ? (
        <span aria-hidden="true" className="text-danger">
          {" "}
          *
        </span>
      ) : null}
    </>
  );
  return (
    <Field label={label} hint={mobile ? labels.phoneHint : undefined} error={errorMessage(error)}>
      <Control field={field} register={register} labels={labels} />
    </Field>
  );
}
